import React, { useEffect, useState, useCallback } from 'react';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiArrowUp, FiArrowDown } from 'react-icons/fi';

// Friendly labels for each `group` discriminator — same 17 groups the
// backend's GET /api/superadmin/config-options/groups returns.
const GROUP_LABELS = {
  clothLabel: 'Cloth Labels',
  shirtField: 'Shirt Measurement Fields',
  trouserField: 'Trouser Measurement Fields',
  napel: 'Napel',
  collarPoint: 'Collar Point',
  collar: 'Collar',
  chestStyle: 'Chest Style',
  frontStyle: 'Front Style',
  namePlate: 'Name Plate',
  frontPocket: 'Front Pocket',
  sleevePocket: 'Sleeve Pocket',
  rutba: 'Rutba',
  flop: 'Flop',
  cuffStyle: 'Cuff Style',
  pantWaistStyle: 'Pant Waist Style',
  pantBottomStyle: 'Pant Bottom Style',
  pantPocketStyle: 'Pant Pocket Style',
  productCategory: 'Product Categories',
};

const FIELD_KEY_GROUPS = ['shirtField', 'trouserField'];

// `value` is the canonical identifier stored on every order — never translated.
// `labels` is the display text per app language; en falls back to value, and
// hi/ar fall back to en in the app when left blank.
const emptyForm = { value: '', labels: { en: '', hi: '', ar: '' }, fieldKey: '' };

const labelsOf = (o) => ({
  en: (o.labels && o.labels.en) || o.label || '',
  hi: (o.labels && o.labels.hi) || '',
  ar: (o.labels && o.labels.ar) || '',
});

export default function ConfigOptions() {
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState('clothLabel');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editOption, setEditOption] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    superAdminApi.get('/config-options/groups')
      .then(r => setGroups(r.data.groups))
      .catch(() => toast.error('Failed to load groups'));
  }, []);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superAdminApi.get('/config-options', { params: { group } });
      setOptions(r.data.options);
    } catch { toast.error('Failed to load options'); }
    setLoading(false);
  }, [group]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  const openAdd = () => { setForm({ value: '', labels: { en: '', hi: '', ar: '' }, fieldKey: '' }); setEditOption(null); setShowModal(true); };
  const openEdit = (o) => { setForm({ value: o.value, labels: labelsOf(o), fieldKey: o.fieldKey || '' }); setEditOption(o); setShowModal(true); };

  const setLabel = (lang, v) => setForm((f) => ({ ...f, labels: { ...f.labels, [lang]: v } }));

  // Ask the backend translation service to suggest the languages left blank,
  // using whichever field the admin filled (or `value`) as the source.
  // Only empty fields are filled — anything the admin already typed is kept.
  const autoTranslate = async () => {
    const src = form.labels.en || form.labels.hi || form.labels.ar || form.value;
    if (!src.trim()) return toast.error('Enter a value or one label first');
    setTranslating(true);
    try {
      const r = await superAdminApi.post('/config-options/translate', { labels: form.labels, value: form.value });
      const s = r.data.labels || {};
      setForm((f) => ({
        ...f,
        labels: {
          en: f.labels.en || s.en || '',
          hi: f.labels.hi || s.hi || '',
          ar: f.labels.ar || s.ar || '',
        },
      }));
      if (r.data.provider === 'none') toast('Translation service not configured — fill manually', { icon: 'ℹ️' });
      else toast.success('Translations suggested — review before saving');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-translate failed — fill manually');
    }
    setTranslating(false);
  };

  const handleSave = async () => {
    if (!form.value.trim()) return toast.error('Value is required');
    setSaving(true);
    try {
      const payload = {
        value: form.value,
        fieldKey: form.fieldKey,
        labels: form.labels,
        label: form.labels.en, // legacy field kept in sync for old consumers
      };
      if (editOption) {
        await superAdminApi.put(`/config-options/${editOption._id}`, payload);
        toast.success('Option updated');
      } else {
        await superAdminApi.post('/config-options', { ...payload, group });
        toast.success('Option added');
      }
      setShowModal(false);
      fetchOptions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id, value) => {
    if (!window.confirm(`Delete "${value}"? This cannot be undone.`)) return;
    try {
      await superAdminApi.delete(`/config-options/${id}`);
      toast.success('Option deleted');
      fetchOptions();
    } catch { toast.error('Delete failed'); }
  };

  const toggleOption = async (id) => {
    try {
      await superAdminApi.put(`/config-options/${id}/toggle`);
      fetchOptions();
    } catch { toast.error('Failed to update'); }
  };

  const move = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= options.length) return;
    const reordered = [...options];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    setOptions(reordered);
    try {
      await superAdminApi.put('/config-options/reorder', { group, orderedIds: reordered.map(o => o._id) });
    } catch {
      toast.error('Reorder failed');
      fetchOptions();
    }
  };

  const showFieldKey = FIELD_KEY_GROUPS.includes(group);

  return (
    <div>
      <div className="filter-tabs" style={{ marginBottom: 20, flexWrap: 'wrap' }}>
        {groups.map(g => (
          <button key={g} className={`filter-tab ${group === g ? 'active' : ''}`}
            onClick={() => setGroup(g)}>{GROUP_LABELS[g] || g}</button>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>{options.length} option(s) in {GROUP_LABELS[group] || group}</div>
        <button className="btn btn-accent" onClick={openAdd}><FiPlus size={15} /> Add Option</button>
      </div>

      <div className="card">
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Value</th>
                  <th>English</th>
                  <th>العربية</th>
                  {showFieldKey && <th>Field Key</th>}
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {options.length === 0 && (
                  <tr><td colSpan={showFieldKey ? 8 : 7}>
                    <div className="empty-state">
                      <h3>No options yet</h3>
                      <p>Add the first value for {GROUP_LABELS[group] || group}</p>
                    </div>
                  </td></tr>
                )}
                {options.map((o, i) => {
                  const L = labelsOf(o);
                  const dash = <span style={{ color: '#94a3b8' }}>—</span>;
                  return (
                  <tr key={o._id}>
                    <td>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button className="btn btn-outline btn-sm btn-icon" disabled={i === 0} onClick={() => move(i, -1)}>
                          <FiArrowUp size={12} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" disabled={i === options.length - 1} onClick={() => move(i, 1)}>
                          <FiArrowDown size={12} />
                        </button>
                      </div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{o.value}</td>
                    <td>{L.en || dash}</td>
                    <td dir="rtl" style={{ textAlign: 'right' }}>{L.ar || dash}</td>
                    {showFieldKey && <td><code style={{ fontSize: 12 }}>{o.fieldKey || '—'}</code></td>}
                    <td>
                      {o.isActive
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-danger">Disabled</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-outline btn-sm btn-icon" title="Edit" onClick={() => openEdit(o)}>
                          <FiEdit2 size={13} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Toggle Active" onClick={() => toggleOption(o._id)}>
                          {o.isActive ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Delete"
                          style={{ color: '#ef4444', borderColor: '#fecaca' }}
                          onClick={() => handleDelete(o._id, o.value)}>
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editOption ? 'Edit Option' : 'Add Option'}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Value <span className="required">*</span></label>
                <input className="form-control" placeholder="e.g. Kuliya Harbia"
                  value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} />
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="form-label" style={{ margin: 0 }}>Display Labels</label>
                  <button type="button" className="btn btn-outline btn-sm"
                    onClick={autoTranslate} disabled={translating}>
                    {translating ? 'Translating…' : '↻ Auto-translate'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 8px' }}>
                  Fill any one language, then click Auto-translate to suggest the others.
                  It only fills <em>blank</em> fields — anything you typed is never overwritten.
                  Review and edit the suggestions, then Save. Nothing is translated on save;
                  a language left blank falls back to English in the app.
                </div>
                <label className="form-label" style={{ fontSize: 12 }}>English</label>
                <input className="form-control" placeholder="Defaults to value if left blank"
                  value={form.labels.en} onChange={e => setLabel('en', e.target.value)} />
                <label className="form-label" style={{ fontSize: 12, marginTop: 8 }}>العربية (Arabic)</label>
                <input className="form-control" dir="rtl" style={{ textAlign: 'right' }}
                  placeholder="Arabic label"
                  value={form.labels.ar} onChange={e => setLabel('ar', e.target.value)} />
              </div>
              {showFieldKey && (
                <div className="form-group">
                  <label className="form-label">Field Key</label>
                  <input className="form-control" placeholder="e.g. chest, pantLength"
                    value={form.fieldKey} onChange={e => setForm({ ...form, fieldKey: e.target.value })} />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    Maps to the measurement key stored on the customer's order.
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editOption ? 'Update' : 'Add Option')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
